import { FlatButton } from '@components/ui'
import TextField from '@components/ui/TextField'
import { Message } from '@core/service/message'
import React, { useEffect, useRef, useState } from 'react'
import { ArticleLayout } from '@components/common'
import { PracticalInformation } from '@components/sections/home'
import { withLayout, withProps } from '@utils/hoc'
import { PageComponent } from 'types/app'
import { Box, Typography } from '@mui/material'
import * as yup from 'yup'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { ReCaptcha, loadReCaptcha } from 'react-recaptcha-v3'

interface IFormInputs {
  email: string
  firstname: string
  lastname: string
  message: string
  subject: string
}

const SITE_KEY = process.env.RECAPTCHA_KEY

const defaultValues: IFormInputs = {
  email: '',
  firstname: '',
  lastname: '',
  message: '',
  subject: ''
}

const schema = yup.object().shape({
  lastname: yup
    .string()
    .matches(/.{2,}/, {
      message: 'Le nom doit comporter au moins 2 caractères!'
    })
    .required('Le nom est requis!'),
  firstname: yup
    .string()
    .matches(/.{2,}/, {
      message: 'Le prénom doit comporter au moins 2 caractères!'
    })
    .required('Le prénom est requis!'),
  email: yup.string().email('L‘email n‘est pas valide!').required('L‘email est requis!'),
  message: yup
    .string()
    .matches(/.{10,}/, {
      message: 'Le message doit comporter au moins 10 caractères!'
    })
    .required('Le message est requis!'),
  subject: yup
    .string()
    .matches(/.{2,}/, {
      message: 'Le sujet doit comporter au moins 2 caractères!'
    })
    .required('Le sujet est requis!')
})

const Contact: PageComponent = () => {
  const recaptchaRef = useRef<ReCaptcha>()
  const recaptchaValue = useRef<string>()

  const [formValues] = useState<IFormInputs>(defaultValues)

  const { email, firstname, lastname, message, subject } = formValues

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<IFormInputs>({
    resolver: yupResolver(schema)
  })

  const verifyCallback = token => {
    recaptchaValue.current = token
  }

  const onSubmit: SubmitHandler<IFormInputs> = async data => {
    const headers = new Headers({
      'Content-Type': 'application/json'
    })

    fetch('/api/contact-form/public?response=' + recaptchaValue.current, {
      method: 'POST',
      mode: 'cors',
      headers,
      body: JSON.stringify({ content: data.message, firstname: data.firstname, lastname: data.lastname, replyTo: data.email, subject: data.subject })
    })
      .then(response => {
        const { status } = response

        if (status !== 200) {
          response
            .json()
            .then(json => {
              // error
              Message.error(json.message ?? json.error)
            })
            .catch(error => {
              console.log('Error json caught: ', error)
              // eslint-disable-next-line prettier/prettier
              Message.error('L\'email n\'a pas pu être envoyé')
            })

          return
        }

        Message.success('Votre message a bien été envoyé')

        // reset form and get a new recaptcha token
        reset(defaultValues)
        recaptchaRef.current.execute()
      })
      .catch(error => {
        console.log('Error caught: ', error)
        Message.error(error)
      })
  }

  const getForm = () => {
    return (
      <form method="POST" onSubmit={handleSubmit(onSubmit)}>
        <div className="mx-[30px] flex flex-1 flex-col space-y-8">
          <div className="flex flex-col sm:gap-8 md:flex-row mobile:space-y-8">
            <TextField id="lastname" label="Nom *" name="lastname" placeholder="Entrer mon nom" fullWidth control={control} value={lastname} />
            <TextField id="firstname" label="Prénom *" name="firstname" placeholder="Entrer mon prénom" fullWidth control={control} value={firstname} />
          </div>
          <div className="flex flex-col space-y-8 md:flex-row">
            <TextField id="email" label="Email *" name="email" placeholder="Entrer mon email" fullWidth type="email" control={control} value={email} />
          </div>
          <div className="flex flex-col space-y-8 md:flex-row">
            <TextField id="subject" label="Sujet *" name="subject" placeholder="Entrer un sujet" fullWidth control={control} value={subject} />
          </div>
          <div className="flex flex-col space-y-8 md:flex-row">
            <TextField
              id="message"
              label="Message *"
              name="message"
              placeholder="Entrer mon message"
              fullWidth
              multiline
              minRows={6}
              maxRows={10}
              control={control}
              value={message}
            />
          </div>
        </div>

        <div>
          <ReCaptcha ref={recaptchaRef} sitekey={SITE_KEY} action="register" verifyCallback={verifyCallback} />
        </div>

        <div className="mt-8 flex flex-col items-center space-y-8">
          <div className="flex flex-col space-y-8">
            <FlatButton variant="contained" color="primary" type="submit" className="max-w-[350px] font-bold normal-case sm:w-[350px]">
              Envoyer
            </FlatButton>
            <Typography>* Tous les champs sont obligatoires</Typography>
          </div>
          <div className="mx-[30px] text-center">
            <Typography className="text-sm text-super-ya-greengrey">
              Ce site est protégé par reCAPTCHA et la <a href="https://policies.google.com/privacy">politique de confidentialité</a> et les{' '}
              <a href="https://policies.google.com/terms">conditions d'utilisation</a> de Google s'appliquent.
            </Typography>
          </div>
        </div>
      </form>
    )
  }

  useEffect(() => {
    for (const error of Object.values<any>(errors)) {
      if (error?.ref) {
        let firstErrorElement = document.getElementsByName(error.ref.name)[0]
        firstErrorElement.scrollIntoView({ behavior: 'smooth' })
        break
      }
    }
  }, [errors])

  useEffect(() => {
    loadReCaptcha(SITE_KEY)
  }, [])

  return (
    <>
      <div className="mx-auto mt-12.5 w-full max-w-[790px] sm:px-[10px] md:w-[790px]">
        <Typography className="mx-[30px] mb-9">N’hésitez pas à nous contacter, nous vous répondrons au plus vite.</Typography>

        {getForm()}
      </div>

      <div className="container mx-auto mt-[40px] flex flex-col items-stretch justify-between space-y-[40px]">
        <div className="bg-super-ya-blue11 px-[25px] py-[35px] 2xs:mx-[10px] xs:mx-[10px] sm:mx-auto 2xl:w-[1536px]">
          <Box bgcolor="transparent">
            <PracticalInformation />
          </Box>
        </div>
      </div>
    </>
  )
}

const Layout = withProps(ArticleLayout, {
  title: 'Contact'
})

export default withLayout(Contact, Layout)
